// Admin v2 — Profilom (Figma ADM_21): saját szint, XP, CP; Áttekintés / XP történet / Achievementek / Üzemeltetés.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, doc, getDocs, limit, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { fmtInt, AdminUser, normalizeUser, roleOf, ROLE_LABEL, ROLE_TONE, timeAgo, toDate } from '../data';
import { Avatar, Badge, Button, Card, Empty, PageHeader, Spinner, StatCard, Tabs } from '../ui';
import { AchievementsTab, StatsTab, XpRow } from './PlayerProfilePage';

export default function MyProfilePage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [u, setU] = useState<AdminUser | null>(null);
  const [tab, setTab] = useState<'overview' | 'xp' | 'achievements' | 'operation'>('overview');
  const [xp, setXp] = useState<(XpRow & { sub: string })[] | null>(null);
  const [earned, setEarned] = useState<Record<string, Date | null> | null>(null);
  const [clubs, setClubs] = useState<{ id: string; name: string; city: string }[] | null>(null);

  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(doc(db, 'users', user.uid), (s) => s.exists() && setU(normalizeUser(s.id, s.data())), () => undefined);
    getDocs(query(collection(db, 'xp_transactions'), where('userId', '==', user.uid), limit(200))).then((s) => setXp(s.docs.map((d) => {
      const v = d.data();
      return { id: d.id, label: String(v.description ?? v.reason ?? 'XP'), sub: String(v.sub ?? ''), amount: Number(v.amount ?? 0), at: toDate(v.createdAt ?? v.timestamp) };
    }).sort((a, b) => (b.at?.getTime() ?? 0) - (a.at?.getTime() ?? 0)))).catch(() => setXp([]));
    getDocs(query(collection(db, 'user_achievements'), where('userId', '==', user.uid), limit(100))).then((s) => {
      const m: Record<string, Date | null> = {};
      s.docs.forEach((d) => { m[String(d.data().badgeId ?? '')] = toDate(d.data().earnedAt); });
      setEarned(m);
    }).catch(() => setEarned({}));
    getDocs(query(collection(db, 'clubs'), where('ownerUid', '==', user.uid))).then((s) => setClubs(s.docs.map((d) => ({ id: d.id, name: String(d.data().name ?? 'Klub'), city: String(d.data().city ?? '') })))).catch(() => setClubs([]));
    return unsub;
  }, [user]);

  if (!u) return <Spinner />;
  const role = roleOf(u);
  const lvl = Math.floor(Math.max(0, u.dqScore) / 50) + 1;
  const next = (Math.floor(u.totalXP / 1000) + 1) * 1000;
  const pct = ((u.totalXP % 1000) / 1000) * 100;
  const total = u.wins + u.losses;

  return (
    <div>
      <PageHeader title="Profilom" sub="Szint, XP és tevékenységek" right={<Button kind="ghost" icon="PencilSimpleLine" onClick={() => nav(`/users/${u.uid}`)}>Adatok szerkesztése</Button>} />
      <Card className="!p-6 flex items-center gap-5 flex-wrap">
        <Avatar src={u.avatarUrl} name={u.displayName} size={80} />
        <div className="flex-1 min-w-[260px]">
          <div className="text-[22px] font-bold text-white">{u.username || u.displayName}</div>
          <div className="flex gap-2 mt-1.5"><Badge tone={ROLE_TONE[role]}>{ROLE_LABEL[role]}</Badge><Badge tone="gold">CP{lvl} · {fmtInt(u.dqScore)}</Badge></div>
          <div className="flex items-center gap-3 mt-3">
            <span className="text-[13px] font-semibold text-white whitespace-nowrap">Szint {u.dqLevel}</span>
            <div className="flex-1 h-2 rounded-full bg-[#31383D] overflow-hidden max-w-[360px]"><div className="h-full rounded-full bg-gradient-to-r from-[#a855f7] to-[#00AFF2]" style={{ width: `${pct}%` }} /></div>
            <span className="text-[12px] text-[#8a8f99] whitespace-nowrap">{fmtInt(next - u.totalXP)} XP a következő szintig</span>
          </div>
        </div>
      </Card>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
        <StatCard icon="Sparkle" tone="purple" label="Összes XP" value={fmtInt(u.totalXP)} valueColor="#a855f7" />
        <StatCard icon="Medal" tone="gold" label="Jelenlegi szint" value={fmtInt(u.dqLevel)} valueColor="#d9a514" />
        <StatCard icon="Trophy" tone="green" label="Győzelmi arány" value={total ? `${Math.round((u.wins / total) * 100)}%` : '—'} valueColor="#2bc48a" />
      </div>
      <div className="mt-6">
        <Tabs value={tab} onChange={setTab} options={[{ value: 'overview', label: 'Áttekintés' }, { value: 'xp', label: 'XP történet' }, { value: 'achievements', label: 'Achievementek' }, { value: 'operation', label: 'Üzemeltetés' }]} />
      </div>
      {tab === 'overview' && <StatsTab u={u} xp={xp} />}
      {tab === 'xp' && (
        <div className="bg-[#15191E] border border-[#31383D] rounded-2xl overflow-hidden overflow-x-auto">
          {xp === null ? <Spinner /> : (
            <table className="w-full min-w-[640px] text-left">
              <thead><tr className="bg-[#1d232a] text-[12px] uppercase text-[#8a8f99]"><th className="pl-5 pr-4 h-[39px] font-semibold">Esemény</th><th className="px-4 font-semibold">Részletek</th><th className="px-4 font-semibold">XP</th><th className="px-4 font-semibold">Időpont</th></tr></thead>
              <tbody>
                {xp.map((x) => (
                  <tr key={x.id} className="border-t border-[#31383D] h-[54px] text-[13px]">
                    <td className="pl-5 pr-4 font-semibold text-white">{x.label}</td>
                    <td className="px-4 text-[#B8B8C3]">{x.sub || '—'}</td>
                    <td className="px-4"><Badge tone={x.amount >= 0 ? 'green' : 'red'}>{x.amount >= 0 ? '+' : '−'}{fmtInt(Math.abs(x.amount))} XP</Badge></td>
                    <td className="px-4 text-[#B8B8C3]">{timeAgo(x.at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {xp?.length === 0 && <Empty>Még nincs XP-mozgás.</Empty>}
        </div>
      )}
      {tab === 'achievements' && <AchievementsTab earned={earned} />}
      {tab === 'operation' && (
        clubs === null ? <Spinner /> : clubs.length === 0 ? <Card><Empty>Nincs saját klubod. Adminként a Klub panelen bármelyik klubot megtekintheted.</Empty></Card> : (
          <div className="flex flex-col gap-2.5">
            {clubs.map((c) => (
              <Card key={c.id} className="!p-4 flex items-center justify-between gap-3">
                <div><div className="text-[14px] font-semibold text-white">{c.name}</div><div className="text-[12px] text-[#8a8f99]">{c.city || '—'}</div></div>
                <Button kind="ghost" onClick={() => nav('/club')}>Klub panel</Button>
              </Card>
            ))}
          </div>
        )
      )}
    </div>
  );
}
